// ----------------------------------------------------------------------- imports externos
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import { TIPOS, nombreDeTipo, tipoSugerido } from "../shared/dibujosHerramienta";
import DibujoHerramienta from "../components/taller/DibujoHerramienta";
import FotosHerramienta from "../components/taller/FotosHerramienta";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import "../assets/css/herramientas.css";
import {
  codigoSugerido,
  darDeBaja,
  guardarHerramienta,
  lugarLibre,
  padron as traerPadron,
} from "../services/herramientas";
import { primerasFotos, subirFotos } from "../services/herramientasFotos";
import { nombrePanol, PANOL_POR_DEFECTO, PANOLES } from "../shared/entregasPanol";

/* El padrón de herramientas: dar de alta, editar y dar de baja.
 *
 * ── ACÁ NO SE ACOMODA EL TABLERO ─────────────────────────────────────────────
 *
 * La posición se toca arrastrando, en el tablero mismo («Editar tablero»). Dos
 * lugares para lo mismo —cuatro cajitas de números acá y el arrastre allá—
 * terminan en que alguien acomoda por un lado, alguien por el otro, y el
 * último que guarda pisa al primero. Una herramienta nueva cae abajo de todo y
 * de ahí se la arrastra a su lugar.
 *
 * ── NO HAY BOTÓN DE BORRAR ───────────────────────────────────────────────────
 *
 * Los préstamos apuntan a la herramienta: borrarla se llevaría puesto el
 * historial de quién la tuvo, que es justamente lo que este sistema vino a
 * guardar. Se da de BAJA, que la saca del tablero y deja el pasado en pie.
 *
 * ── FUERA DE SERVICIO NO ES BAJA ─────────────────────────────────────────────
 *
 * Una herramienta rota SIGUE EN EL TABLERO, en gris y con el motivo. Sacarla
 * haría que quien la busca piense que no existe y salga a comprar otra; el
 * hueco gris dice «está, pero no la lleves».
 *
 * ── LAS FOTOS SON OPTATIVAS (P-348) ──────────────────────────────────────────
 *
 * Nico, 08/10: «Quiero agregar fotos de herramientas cuando agrego una
 * herramienta». Se pueden sumar varias en el alta y en la edición, y nunca se
 * piden: el 14/09 se habían sacado porque eran obligatorias y cada alta era
 * una sesión de fotos. El dibujo sigue armando el tablero.
 */

const VACIA = {
  codigo: "", nombre: "", tipo: "", categoria: "", sector: "", panol: PANOL_POR_DEFECTO,
  cantidad: 1,
  fuera_de_servicio: false, fuera_motivo: "", activo: true, nota: "",
};

function Ficha({ h, filas, onCerrar, onGuardado }) {
  const [f, setF] = useState(h);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [trabajando, setTrabajando] = useState(false);
  /* Las fotos elegidas en el ALTA esperan acá: hasta guardar no hay id al
     que colgarlas. En la edición se suben en el momento (`FotosHerramienta`). */
  const [pendientes, setPendientes] = useState([]);
  const [subiendo, setSubiendo] = useState(null);
  /* Se dio de alta pero quedó algo que decir de las fotos: la ficha sigue
     abierta, ya como EDICIÓN —un segundo «Guardar» no puede dar de alta otra
     vez— y al cerrarla se recarga la tabla para que aparezca la nueva. */
  const [recienDada, setRecienDada] = useState(false);
  /* MIENTRAS GUARDA NO SE CIERRA. Las fotos del alta suben DESPUÉS de dar de
     alta la herramienta: cerrarla a mitad dejaba la nueva fuera de la tabla
     y, al terminar en segundo plano, `onGuardado` cerraba la ficha que
     estuviera abierta en ese momento (otra alta a medio cargar). */
  const cerrar = trabajando ? () => {} : recienDada ? onGuardado : onCerrar;

  const campo = (k, v) => setF((x) => ({ ...x, [k]: v }));

  /* EL DIBUJO SE PROPONE AL ESCRIBIR EL NOMBRE, y se puede cambiar. Cargar
     treinta herramientas eligiendo el dibujo de cada una en una lista de
     veintitrés es treinta veces la misma búsqueda; «Llave combinada 14» ya
     dice cuál es. Sólo se propone mientras nadie eligió a mano: pisarle la
     elección a alguien porque después corrigió una letra del nombre es peor
     que no proponer nada. */
  const [eligioAMano, setEligioAMano] = useState(!!h.tipo);

  /* EL CÓDIGO TAMBIÉN SE PROPONE, y por el mismo motivo que el dibujo: es
     obligatorio —la base lo pide único y no nulo— y el que está parado frente
     a la pared no tiene ninguno. Se propone mientras nadie lo escribió a mano;
     en una herramienta que ya existe NO se toca nunca, porque ese código está
     impreso en la etiqueta que tiene pegada. */
  const [codigoAMano, setCodigoAMano] = useState(!!h.codigo);
  const proponer = (x, tipo, nombre) =>
    (codigoAMano ? x.codigo : codigoSugerido(tipo, nombre, filas));

  const nombrar = (v) => {
    setF((x) => {
      const tipo = eligioAMano ? x.tipo : tipoSugerido(v);
      return { ...x, nombre: v, tipo, codigo: proponer(x, tipo, v) };
    });
  };
  const elegirTipo = (t) => {
    setEligioAMano(true);
    setF((x) => ({ ...x, tipo: t, codigo: proponer(x, t, x.nombre) }));
  };

  const guardar = async () => {
    setError("");
    setAviso("");
    setTrabajando(true);
    const r = await guardarHerramienta(f);
    if (!r.ok) { setTrabajando(false); setError(r.error); return; }

    /* LAS FOTOS DEL ALTA SE SUBEN DESPUÉS DE GUARDAR, con el id que acaba de
       nacer. Si alguna no sube, la herramienta ya quedó guardada: se dice, y
       la ficha queda abierta como edición para volver a probar. */
    if (!f.id && pendientes.length) {
      const fallar = (texto) => {
        setTrabajando(false);
        setSubiendo(null);
        setCodigoAMano(true);
        setEligioAMano(true);
        setRecienDada(true);
        if (r.id) setF((x) => ({ ...x, id: r.id }));
        setAviso(texto);
      };
      if (!r.id) {
        fallar("La herramienta quedó guardada, pero las fotos no se subieron. " +
               "Abrila con «Editar» y agregalas de nuevo.");
        return;
      }
      setSubiendo({ hechas: 0, total: pendientes.length });
      const s = await subirFotos(r.id, pendientes, {
        alProgreso: (hechas, total) => setSubiendo({ hechas, total }),
      });
      setPendientes([]);
      if (!s.ok || s.aviso) {
        /* Cuáles no subieron, porque la lista de elegidas ya se vació; y la
           sección de fotos está ARRIBA de este aviso. Si la base todavía no
           tiene dónde anotarlas, volver a probar no sirve: no se invita. */
        const nombres = (s.fallidas || []).map((x) => x.nombre).filter(Boolean);
        const cuales = !nombres.length ? ""
          : `${nombres.length === 1 ? "No subió" : "No subieron"}: ${nombres.join(", ")}.`;
        fallar(["La herramienta quedó guardada.", s.error, cuales, s.aviso,
                s.ok || s.faltaTabla
                  ? ""
                  : "Las que no subieron se pueden volver a agregar en «Fotos», acá arriba."]
          .filter(Boolean).join(" "));
        return;
      }
    }
    setTrabajando(false);
    setSubiendo(null);
    onGuardado();
  };

  return (
    <div className="hrr-fondo" onClick={cerrar} role="presentation">
      <aside className="hrr-panel" onClick={(e) => e.stopPropagation()}
             role="dialog" aria-label={f.id ? "Editar herramienta" : "Nueva herramienta"}>
        <h2>{f.id ? f.nombre || "Editar" : "Nueva herramienta"}</h2>

        <label className="hrr-campo">
          Código interno
          <input type="text" value={f.codigo} aria-label="Código"
                 onChange={(e) => {
                   setCodigoAMano(true);
                   campo("codigo", e.target.value);
                 }} />
          <span className="hrr-codigo">
            {f.id ? "Es el que está pegado en la herramienta."
                  : "Se propone solo con el nombre. Cambialo si el taller usa otro."}
          </span>
        </label>
        <label className="hrr-campo">
          Nombre
          <input type="text" value={f.nombre} aria-label="Nombre"
                 onChange={(e) => nombrar(e.target.value)} />
        </label>

        {/* QUÉ ES, que es lo que se dibuja: el dibujo arma el tablero y la
            silueta, y eso no cambió. Las fotos (P-348) van aparte, más abajo,
            y siguen sin PEDIRSE: son optativas y no reemplazan al dibujo. */}
        <div className="hrr-campo">
          Qué es
          <div className="hrr-elegir" role="radiogroup" aria-label="Qué es">
            {TIPOS.map((t) => (
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={f.tipo === t}
                aria-label={nombreDeTipo(t)}
                title={nombreDeTipo(t)}
                className={"hrr-elegir-uno" + (f.tipo === t ? " activo" : "")}
                onClick={() => elegirTipo(t)}
              >
                <DibujoHerramienta tipo={t} />
              </button>
            ))}
          </div>
          <span className="hrr-codigo">{nombreDeTipo(f.tipo)}</span>
        </div>
        <label className="hrr-campo">
          Categoría
          <input type="text" value={f.categoria || ""} aria-label="Categoría"
                 placeholder="Manuales, Eléctricas, Medición…"
                 onChange={(e) => campo("categoria", e.target.value)} />
        </label>
        <label className="hrr-campo">
          Sector
          <input type="text" value={f.sector || ""} aria-label="Sector"
                 placeholder="Taller, Puerto, Pañol…"
                 onChange={(e) => campo("sector", e.target.value)} />
        </label>
        {/* P-311 (Nico): «las herramientas de préstamos debe diferenciar entre
            pañoles». De qué pañol es esta herramienta y su stock; la misma en
            dos pañoles se carga dos veces, con dos códigos. */}
        <label className="hrr-campo">
          Pañol
          <select value={f.panol || ""} aria-label="Pañol"
                  onChange={(e) => campo("panol", e.target.value || null)}>
            <option value="">Sin pañol</option>
            {PANOLES.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
          </select>
        </label>

        {/* CUÁNTAS HAY. Hasta el 16/09 el tablero suponía una de cada cosa y
            una herramienta prestada no se podía volver a prestar. Nico: «No
            puedo entregar más de un guante o un destornillador». Con 1 se
            comporta igual que antes; con 20, salen veinte y la veintiuna
            espera a que vuelva alguna. El tope lo cuenta la base. */}
        <label className="hrr-campo">
          Cuántas hay
          <input type="number" min="1" step="1"
                 value={f.cantidad ?? 1}
                 aria-label="Cantidad"
                 onChange={(e) => campo("cantidad", Number(e.target.value) || 1)} />
          <span className="hrr-codigo">
            Cuántas unidades iguales tiene el pañol. Se pueden prestar todas a
            la vez, a personas distintas.
          </span>
          {/* Nico, 20/10: en la caja, «Guante Tela» decía «no queda
              ninguna» (había una y estaba entregada), subió este número a 48
              y recién ahí pudo entregarle a Romero. Eso era lo que había que
              hacer: este número es el tope de lo que se puede entregar
              (`taller_prestamo_cabe`). Se dice acá, junto con que el stock
              del depósito es otra cuenta, porque la palabra «stock» de la
              caja lo había mandado a buscar al lugar equivocado. */}
          <span className="hrr-codigo">
            Si en la caja dice «no queda ninguna» y llegaron más, subí este
            número: es lo que deja entregar. El stock del depósito es otra
            cuenta y no cambia lo que se puede entregar.
          </span>
        </label>

        {/* NO SE PIDE A LOS CUÁNTOS DÍAS QUEDA DEMORADA. Hasta el 06/10 la
            ficha tenía ese campo; Nico, por Igna: no le interesa, que se saque.
            La regla de «demorada» sigue en la base (`taller_tablero`) con el
            `dias_prestamo` de cada fila: una nueva nace con el default de la
            columna (7) y una que ya existe conserva el que tenía, porque el
            servicio no lo manda ni en el alta ni en la edición. */}

        <label className="hrr-campo">
          Nota
          <input type="text" value={f.nota || ""} aria-label="Nota"
                 onChange={(e) => campo("nota", e.target.value)} />
        </label>

        <label className="hrr-campo">
          <input type="checkbox" checked={!!f.fuera_de_servicio}
                 aria-label="Fuera de servicio"
                 onChange={(e) => campo("fuera_de_servicio", e.target.checked)} />
          {" "}Fuera de servicio
        </label>
        {f.fuera_de_servicio && (
          <label className="hrr-campo">
            Por qué
            <input type="text" value={f.fuera_motivo || ""} aria-label="Motivo"
                   placeholder="Rota, en reparación, prestada a otra sede…"
                   onChange={(e) => campo("fuera_motivo", e.target.value)} />
          </label>
        )}

        {/* FOTOS (OPTATIVAS), P-348. En el alta se juntan y suben al guardar;
            en la edición suben al elegirlas. */}
        <FotosHerramienta
          herramientaId={f.id || null}
          nombre={f.nombre}
          puedeEditar
          pendientes={pendientes}
          onPendientes={setPendientes}
          ocupado={trabajando}
        />

        {error && <p className="hrr-aviso">{error}</p>}
        {aviso && <p className="hrr-aviso hrr-aviso-wa" role="status">{aviso}</p>}

        <div className="hrr-acciones">
          <button type="button" className="hrr-btn" onClick={cerrar} disabled={trabajando}>
            {recienDada ? "Cerrar" : "Cancelar"}
          </button>
          {/* Dada de alta sin id de vuelta: otro «Guardar» la daría de alta
              dos veces. Se cierra y se edita desde la tabla. */}
          <button type="button" className="hrr-btn hrr-btn--principal"
                  disabled={trabajando || (recienDada && !f.id)} onClick={guardar}>
            {subiendo
              ? `Subiendo fotos ${subiendo.hechas} de ${subiendo.total}…`
              : trabajando ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </aside>
    </div>
  );
}

const HerramientasAdmin = () => {
  const { permisos = null } = useOutletContext() || {};
  const permitido = permisos !== null && permisos.includes("cargar.taller");

  const [filas, setFilas] = useState(undefined);
  const [error, setError] = useState("");
  const [ficha, setFicha] = useState(null);
  const [primeras, setPrimeras] = useState({});

  const cargar = useCallback(async () => {
    const [r, f] = await Promise.all([traerPadron(), primerasFotos()]);
    setFilas(r.filas);
    setError(r.error);
    setPrimeras(f.porHerramienta || {});
  }, []);

  useEffect(() => { if (permitido) cargar(); }, [permitido, cargar]);

  const nueva = useCallback(
    () => setFicha({ ...VACIA, ...lugarLibre(filas || []) }), [filas]);

  /* SE PUEDE LLEGAR ACÁ CON LA FICHA YA ABIERTA (`?nueva=1`). Es lo que aprieta
     el que está mirando el tablero y quiere sumar una herramienta: el alta no
     vive en el tablero, así que el botón de allá trae para acá — y traer a una
     tabla, para que apriete otro botón igual, es mandarlo a buscar la puerta
     de nuevo. Se abre UNA vez y recién cuando el padrón está leído, porque el
     lugar de la nueva se calcula con lo que ya hay. */
  const [params, setParams] = useSearchParams();
  const abierta = useRef(false);
  useEffect(() => {
    if (abierta.current || !permitido || filas === undefined) return;
    if (params.get("nueva") !== "1") return;
    abierta.current = true;
    nueva();
    const otros = new URLSearchParams(params);
    otros.delete("nueva");
    setParams(otros, { replace: true });
  }, [params, setParams, permitido, filas, nueva]);

  if (!permitido) {
    return <PuertaCerrada />;
  }

  const baja = async (h) => {
    const r = await darDeBaja(h.id, h.activo);
    if (!r.ok) { setError(r.error); return; }
    cargar();
  };

  return (
    <div className="hrr">
      <div className="hrr-cabecera">
        <h1 className="hrr-titulo">Herramientas · administrar</h1>
        <div className="hrr-cuenta">
          <Link className="hrr-btn" to="/taller/herramientas">Ver las cajas</Link>
          <button type="button" className="hrr-btn hrr-btn--principal"
                  onClick={nueva}>
            Agregar herramienta
          </button>
        </div>
      </div>

      {error && (
        <p className="hrr-aviso">No se pudo consultar.</p>
      )}
      {filas === undefined && <p className="hrr-vacio">Cargando…</p>}
      {filas && filas.length === 0 && !error && (
        <div className="hrr-vacio">
          <h3>Todavía no hay ninguna herramienta</h3>
          <p>Agregá la primera y después acomodalas en el tablero.</p>
        </div>
      )}

      {filas && filas.length > 0 && (
        <table className="hrr-tabla">
          <thead>
            <tr>
              <th>Foto</th><th>Código</th><th>Nombre</th><th>Categoría</th><th>Sector</th>
              <th>Pañol</th><th>Cantidad</th><th>Estado</th><th />
            </tr>
          </thead>
          <tbody>
            {filas.map((h) => (
              <tr key={h.id} className={h.activo ? "" : "hrr-fila--baja"}>
                <td>
                  {primeras[h.id] && (
                    <a className="hrr-mini-link" href={primeras[h.id].url}
                       target="_blank" rel="noreferrer"
                       title={primeras[h.id].cuantas > 1
                         ? `${primeras[h.id].cuantas} fotos` : "1 foto"}>
                      <img className="hrr-mini" src={primeras[h.id].url}
                           alt={`Foto de ${h.nombre}`} />
                      {primeras[h.id].cuantas > 1 && (
                        <span className="hrr-mini-mas">+{primeras[h.id].cuantas - 1}</span>
                      )}
                    </a>
                  )}
                </td>
                <td className="hrr-codigo">{h.codigo}</td>
                <td>{h.nombre}</td>
                <td>{h.categoria || "—"}</td>
                <td>{h.sector || "—"}</td>
                <td>{nombrePanol(h.panol, { corto: true })}</td>
                <td>{h.cantidad ?? 1}</td>
                <td>
                  {!h.activo ? "De baja"
                    : h.fuera_de_servicio ? "Fuera de servicio" : "En el tablero"}
                </td>
                <td>
                  <button type="button" className="hrr-btn"
                          onClick={() => setFicha(h)}>Editar</button>
                  {" "}
                  <button type="button" className="hrr-btn" onClick={() => baja(h)}>
                    {h.activo ? "Dar de baja" : "Reactivar"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {ficha && (
        <Ficha
          h={ficha}
          filas={filas || []}
          onCerrar={() => setFicha(null)}
          onGuardado={() => { setFicha(null); cargar(); }}
        />
      )}
    </div>
  );
};

export default HerramientasAdmin;
